import { RetellClient } from 'retell-client-js-sdk';
import { getVoiceCallMetadata, registerVoiceCall, stopVoiceCall, validateVoiceConnection } from '../services/voiceApi';

// Shared by the React app and the public window.QurveSDK bundle. apiKey is a
// Qurve tenant key; the private Retell key is held only by the Django backend.
export class QurveSDK {
  constructor(config = {}) {
    this.config = config;
    this.baseUrl = config.baseUrl || (config.env === 'production'
      ? 'https://api.qurve.ai/api/' : 'https://staging.qurve.ai/api/');
    this.auth = { apiKey: config.apiKey };
    this.session = null;
    this.startPromise = null;
    this.metadataTimer = null;
    this.metadataRequest = null;
  }

  async init() {
    // Retained for existing Qurve SDK consumers; no script or widget is injected.
  }

  startCall() {
    if (this.startPromise) return this.startPromise;
    if (this.session && this.session.status !== 'ended') return Promise.resolve(this.session);

    const client = new RetellClient({
      key: 'server-managed',
      // Override REST creation only. Retell's signaling URL remains unchanged.
      fetch: async (url, options) => {
        const path = new URL(String(url)).pathname;
        let data;
        if (path === '/v3/create-web-call') {
          const connection = this.config.connection || (typeof this.config.token === 'object' && this.config.token);
          if (connection) {
            data = validateVoiceConnection(connection);
          } else if (this.config.token) {
            // A v2 token alone cannot be joined with the v3 gateway transport.
            throw new Error('Pass the full v3 response as connection, not just a token.');
          } else {
            const request = {
              originating_domain: window.location.hostname,
              ...JSON.parse(options.body),
              ...(this.config.sessionId ? { session_id: this.config.sessionId } : {}),
            };
            data = validateVoiceConnection(await (this.config.registerCall
              ? this.config.registerCall(request)
              : registerVoiceCall(this.baseUrl, this.auth, request)));
          }
        } else if (/^\/v2\/stop-call\/[^/]+$/.test(path)) {
          const callId = decodeURIComponent(path.split('/').pop());
          data = await (this.config.stopCall
            ? this.config.stopCall(callId) : stopVoiceCall(this.baseUrl, this.auth, callId));
        } else {
          throw new Error('Unsupported voice control request');
        }
        return new Response(JSON.stringify(data), {
          status: 200, headers: { 'Content-Type': 'application/json' },
        });
      },
    });

    const session = client.createWebCall({
      // Django selects the authenticated tenant's agent; this is never trusted.
      agent_id: this.config.agentId || 'server-selected',
      metadata: this.config.metadata || {},
      transcript: false,
      hooks: {
        onStatus: (status) => {
          if (this.session !== session) return;
          this.config.onStatus?.(status);
          if (status === 'live') {
            this.config.onCallStart?.();
            this.startMetadataPolling(session);
          }
        },
        onEnd: (event) => {
          if (this.session !== session) return;
          this.stopMetadataPolling();
          this.config.onCallEnd?.(event);
        },
        onError: (error) => {
          if (this.session === session) this.config.onError?.(error);
        },
      },
    });
    this.session = session;
    const starting = session.ready.then(() => {
      if (session.status !== 'live') throw new Error('Call ended before it connected');
      return session;
    }).finally(() => {
      if (this.startPromise === starting) this.startPromise = null;
    });
    this.startPromise = starting;
    return starting;
  }

  async abortCall() {
    this.stopMetadataPolling();
    await this.session?.end();
  }

  muteCall() {
    this.session?.mute();
  }

  unmuteCall() {
    this.session?.unmute();
  }

  startMetadataPolling(session) {
    this.stopMetadataPolling();
    if (!this.config.onMetadata || !session.callId) return;
    const controller = new AbortController();
    this.metadataRequest = controller;
    let cursor = 0;
    let failures = 0;
    const poll = async () => {
      if (controller.signal.aborted || session.status !== 'live') return;
      try {
        const data = await (this.config.getMetadata
          ? this.config.getMetadata(session.callId, cursor, controller.signal)
          : getVoiceCallMetadata(this.baseUrl, this.auth, session.callId, cursor, controller.signal));
        if (controller.signal.aborted || this.session !== session) return;
        for (const event of data.events) {
          if (event.id > cursor) {
            cursor = event.id;
            this.config.onMetadata({ metadata: event.metadata });
          }
        }
        cursor = Math.max(cursor, data.next_cursor);
        failures = 0;
      } catch (error) {
        if (controller.signal.aborted) return;
        failures += 1;
        if (failures === 1) {
          if (this.config.onMetadataError) this.config.onMetadataError(error);
          else console.warn('Voice navigation metadata unavailable:', error.message);
        }
        if ([400, 401, 403, 404].includes(error.status) || failures >= 5) return;
      }
      if (!controller.signal.aborted && session.status === 'live') {
        this.metadataTimer = setTimeout(poll, 1000);
      }
    };
    void poll();
  }

  stopMetadataPolling() {
    clearTimeout(this.metadataTimer);
    this.metadataTimer = null;
    this.metadataRequest?.abort();
    this.metadataRequest = null;
  }
}

export default QurveSDK;
